/**
 * Map Twilio Voice SDK / browser media errors to user-facing dialer copy.
 *
 * Dial verb errors (13xxx, geo-permission 21xxx) are raised on Twilio's
 * servers after the browser WebRTC leg is already up. The Voice JS SDK
 * typically emits `disconnect` with no `error` event and no network failure.
 */

const TWILIO_MESSAGES = {
  13224:
    "Twilio couldn’t call that number. Check that it’s a valid E.164 number, then try again or switch to Forwarding Device.",
  13225:
    "Twilio blocked this outbound call before it reached the destination. This can happen when the number is flagged as high-risk, a regulatory restriction applies, or your Twilio account needs an approved Primary Customer Profile (Trust Hub) to call +1 numbers. Try a different number, or switch to Forwarding Device.",
  20101:
    "There’s a problem with your Twilio credentials or access token. Please update your Twilio settings and try again.",
  21216:
    "Your Twilio account isn’t allowed to call this destination. Check Geographic Permissions in the Twilio Console, then try again or switch to Forwarding Device.",
  21408:
    "Your Twilio account isn’t allowed to call this destination. Enable the destination country in Geographic Permissions, then try again or switch to Forwarding Device.",
  31005:
    "The call connection failed. Please try again, or switch to Forwarding Device.",
  31402:
    "We couldn’t access your microphone for this call. Your browser may already have permission, but the mic stream still failed — check that the correct microphone is selected and working in your browser and operating system, then try again. You can also switch to Forwarding Device.",
};

/** Remote disconnects this soon after Twilio answers the client usually mean Dial failed (e.g. 13225). */
export const UNEXPECTED_DISCONNECT_MAX_MS = 5000;

export function getUnexpectedDisconnectMessage() {
  return "This call ended before it reached the destination. Twilio may have blocked the number, the number may be invalid, or it couldn’t be reached. Check the number and try again, or switch to Forwarding Device.";
}

const DIAL_STATUS_MESSAGES = {
  busy: "The destination was busy. Try again later, or switch to Forwarding Device.",
  "no-answer":
    "The destination didn’t answer. Try again, or switch to Forwarding Device.",
  canceled:
    "The call was canceled before it was answered. Try again, or switch to Forwarding Device.",
};

/**
 * Map a /twilio/lookup-dial-result `dialResult` to dialer copy.
 * Prefers Twilio errorCode; otherwise Dial status; otherwise the generic hangup copy.
 *
 * @param {{ errorCode?: number|string|null, errorMessage?: string|null, status?: string|null } | null | undefined} dialResult
 * @returns {string}
 */
export function getDialResultMessage(dialResult) {
  if (!dialResult) {
    return getUnexpectedDisconnectMessage();
  }

  if (dialResult.errorCode != null && dialResult.errorCode !== "") {
    return getTwilioErrorMessage({
      code: dialResult.errorCode,
      message: dialResult.errorMessage,
    });
  }

  const status = String(dialResult.status || "").toLowerCase();
  if (DIAL_STATUS_MESSAGES[status]) {
    return DIAL_STATUS_MESSAGES[status];
  }

  return getUnexpectedDisconnectMessage();
}

/**
 * Dial-blocked / failed-to-bridge hangups look like a normal SDK `disconnect`.
 * User hangups and established conversations should not show an error.
 *
 * @param {{
 *   userHangup?: boolean,
 *   alreadyErrored?: boolean,
 *   connectedAt?: number | null,
 *   now?: number,
 * }} opts
 * @returns {boolean}
 */
export function shouldSurfaceUnexpectedDisconnect({
  userHangup = false,
  alreadyErrored = false,
  connectedAt = null,
  now = Date.now(),
} = {}) {
  if (userHangup || alreadyErrored) return false;
  if (!connectedAt) return true;
  return now - connectedAt < UNEXPECTED_DISCONNECT_MAX_MS;
}

const TWILIO_ERROR_DOCS_BASE = "https://www.twilio.com/docs/api/errors";

/**
 * Twilio docs URL for codes we map in TWILIO_MESSAGES. Other codes have
 * pages too, but we only link ones we've reviewed copy for.
 *
 * @param {number|string|null|undefined} code
 * @returns {string|null}
 */
export function getTwilioErrorDocsUrl(code) {
  const n = code != null ? Number(code) : NaN;
  if (Number.isNaN(n) || !TWILIO_MESSAGES[n]) return null;
  return `${TWILIO_ERROR_DOCS_BASE}/${n}`;
}

/**
 * Split a `getTwilioErrorMessage` string so the Error Modal can link
 * "Twilio Error {code}" when that code is one we map.
 *
 * @param {string|null|undefined} message
 * @returns {{ code: number, url: string, detail: string } | null}
 */
export function getMappedTwilioErrorDocs(message) {
  const match = String(message || "").match(
    /^Twilio Error (\d+)(:\s*)?([\s\S]*)$/
  );
  if (!match) return null;

  const code = Number(match[1]);
  const url = getTwilioErrorDocsUrl(code);
  if (!url) return null;

  const separator = match[2] || "";
  const rest = match[3] || "";
  return {
    code,
    url,
    detail: rest ? `${separator}${rest}` : "",
  };
}

function formatTwilioError(code, detail) {
  const prefix = `Twilio Error ${code}`;
  return detail ? `${prefix}: ${detail}` : `${prefix}. Please try again or switch to Forwarding Device.`;
}

/**
 * @param {{ code?: number|string, message?: string } | null | undefined} error
 * @returns {string}
 */
export function getTwilioErrorMessage(error) {
  if (!error) {
    return "Something went wrong with browser calling. Please try again.";
  }

  const code = error.code != null ? Number(error.code) : null;
  const twilioMessage =
    typeof error.message === "string" && error.message.trim()
      ? error.message.trim()
      : null;

  if (code != null && !Number.isNaN(code) && TWILIO_MESSAGES[code]) {
    return formatTwilioError(code, TWILIO_MESSAGES[code]);
  }

  if (code != null && !Number.isNaN(code)) {
    return formatTwilioError(
      code,
      twilioMessage
        ? `${twilioMessage}. Please try again or switch to Forwarding Device.`
        : null
    );
  }

  if (twilioMessage) {
    return `Twilio Error: ${twilioMessage}. Please try again or switch to Forwarding Device.`;
  }

  return "Something went wrong with browser calling. Please try again.";
}

/**
 * @param {DOMException | Error | { name?: string, message?: string } | null | undefined} error
 * @returns {string}
 */
export function getMicPreflightErrorMessage(error) {
  const name = error?.name || "";

  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return "Microphone access is blocked. Allow microphone permission for this site in your browser settings, then try again.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No microphone was found. Connect a microphone (or check that it isn’t disabled in your OS), then try again.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Your microphone is in use by another app or couldn’t be started. Close other apps using the mic, check OS sound settings, then try again.";
  }
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
    return "Your microphone doesn’t meet the audio requirements for browser calling. Try the default microphone, then try again.";
  }
  if (name === "SecurityError") {
    return "Microphone access isn’t available in this context. Open Twilanswer over HTTPS and try again.";
  }

  const detail =
    typeof error?.message === "string" && error.message.trim()
      ? error.message.trim()
      : null;

  return detail
    ? `We couldn’t access your microphone (${detail}). Please check browser and OS mic settings, then try again.`
    : "We couldn’t access your microphone. Please check browser and OS mic settings, then try again.";
}

/**
 * Request a short-lived mic stream to validate getUserMedia before Twilio connect.
 * Stops all tracks immediately on success.
 * @returns {Promise<{ ok: true } | { ok: false, error: Error }>}
 */
export async function preflightMicrophone() {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    const error = new Error("getUserMedia is not supported in this browser");
    error.name = "NotSupportedError";
    return { ok: false, error };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // ignore
      }
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}
